import { Bar, BarChart, Cell, LabelList, XAxis, YAxis } from "recharts";

import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import type { WasteStatsBucket } from "@/hooks/use-waste-stats";

import { ChartCard } from "./chart-card";
import { useTranslation } from "@/i18n/use-translation";

/** Literal keys, resolved at render (POS2-1134 CP3a). */
const TIERS = [
  { key: "L1", labelKey: "waste.tierL1", color: "#ef4444" },
  { key: "L2", labelKey: "waste.tierL2", color: "#f59e0b" },
  { key: "L3", labelKey: "waste.tierL3", color: "#3b82f6" },
  { key: "L4", labelKey: "waste.tierL4", color: "#10b981" },
] as const;

export function TierDistributionBar({ buckets }: { buckets: WasteStatsBucket[] }) {
  const { t } = useTranslation();
  const countByKey = new Map(buckets.map((b) => [b.key, b.event_count]));
  const total = buckets.reduce((sum, b) => sum + b.event_count, 0);

  const data = TIERS.map((tier) => {
    const count = countByKey.get(tier.key) ?? 0;
    return { tier: tier.key, color: tier.color, count, pct: total > 0 ? (count / total) * 100 : 0 };
  });

  return (
    <ChartCard
      title={t("waste.tierBarTitle")}
      description={t("waste.tierBarDescription")}
      isEmpty={total === 0}
    >
      <ChartContainer
        config={{ pct: { label: t("waste.shareOfEvents") } }}
        className="h-[300px] w-full"
      >
        <BarChart data={data} margin={{ top: 16 }}>
          <XAxis dataKey="tier" tickLine={false} axisLine={false} tickMargin={8} />
          <YAxis hide />
          <ChartTooltip
            content={
              <ChartTooltipContent
                hideLabel
                formatter={(value, _name, item) =>
                  `${item.payload.tier}: ${Number(value).toFixed(1)}% (${item.payload.count})`
                }
              />
            }
          />
          <Bar dataKey="pct" radius={4}>
            <LabelList
              dataKey="pct"
              position="top"
              className="fill-gray-500"
              formatter={(value: number) => `${value.toFixed(0)}%`}
            />
            {data.map((d) => (
              <Cell key={d.tier} fill={d.color} />
            ))}
          </Bar>
        </BarChart>
      </ChartContainer>
    </ChartCard>
  );
}
